import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Minus, Plus } from "lucide-react";

import type { Instance } from "@/api/client";
import { feetInches } from "@/flow/takeoff";
import { cn } from "@/ui/cn";
import { Kbd } from "@/ui/primitives";
import type { BBoxNorm } from "@/viewer/bbox";
import { BoxSelect } from "@/viewer/BoxSelect";
import { PanelOverlay, unitOf } from "@/viewer/PanelOverlay";
import type { Paint } from "@/viewer/PanelOverlay";
import { fitWidth, mirrorScroll, zoomAbout } from "@/viewer/sync";
import { ZOOM_LEVELS, stepZoom } from "@/viewer/zoomForBox";
import type { Zoom } from "@/viewer/zoomForBox";

export type Layout = "split" | "takeoff" | "original";

interface Props {
  /** The sheet fitted to the pane: the 1568 px raster. */
  src: string;
  /** The sheet at 200 dpi, swapped in once the view is zoomed. */
  srcLarge: string;
  /** Width over height of the sheet; read from the image itself when not given. */
  aspect?: number | undefined;
  name: string;
  instances: readonly Instance[];
  paint: ReadonlyMap<string, Paint>;
  highlightUnitId?: string | null;
  selectedId?: string | null;
  onPick?: (instance: Instance) => void;
  layout: Layout;
  /** When set or changed, both panes centre on this box. */
  focusBox?: BBoxNorm | null;
  /** While set, a drag on the take-off draws a box instead of moving the sheet. */
  onBox?: ((box: BBoxNorm) => void) | null;
  pickedIds?: ReadonlySet<string> | null;
}

interface Tip {
  instance: Instance;
  x: number;
  y: number;
}

const GUTTER = 24;
/** Centres a sheet smaller than its pane; a larger one scrolls from its top-left. */
const CENTRE = "grid min-h-full w-max min-w-full place-items-center p-3";
const ZOOM_BUTTON =
  "grid h-7 min-w-7 place-items-center rounded-md text-ink-2 hover:text-ink disabled:opacity-40";
const PANE_LABEL =
  "flex items-center justify-between gap-2 border-b border-line bg-surface px-3 py-1.5 text-xs font-semibold tracking-wide text-ink-2 uppercase";

/**
 * The same sheet twice, the architect's on the left and the take-off on the right, moving
 * as one: scroll, drag or zoom either and the other follows. Both panes show the server's
 * raster of the page, so a 100 MB set never has to reach the browser to be looked at.
 */
export function SplitView({
  src,
  srcLarge,
  aspect,
  name,
  instances,
  paint,
  highlightUnitId = null,
  selectedId = null,
  onPick,
  layout,
  focusBox = null,
  onBox = null,
  pickedIds = null,
}: Props) {
  const left = useRef<HTMLDivElement | null>(null);
  const right = useRef<HTMLDivElement | null>(null);
  const [pane, setPane] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState<Zoom>(1);
  const [largeReady, setLargeReady] = useState(false);
  const [natural, setNatural] = useState<number | null>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  /** The pane the user is moving; the other one follows and must not answer back. */
  const leader = useRef<HTMLDivElement | null>(null);
  const release = useRef<number | undefined>(undefined);
  const pending = useRef<{
    ratio: number;
    anchor?: { x: number; y: number };
    pane: HTMLDivElement;
  } | null>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number; moved: boolean } | null>(
    null,
  );

  const panes = useCallback(
    () => [left.current, right.current].filter((p): p is HTMLDivElement => p !== null),
    [],
  );

  // Pane nodes in state, so effects see them come and go with the layout.
  const [leftNode, setLeftNode] = useState<HTMLDivElement | null>(null);
  const [rightNode, setRightNode] = useState<HTMLDivElement | null>(null);
  const attachLeft = useCallback((node: HTMLDivElement | null) => {
    left.current = node;
    setLeftNode(node);
  }, []);
  const attachRight = useCallback((node: HTMLDivElement | null) => {
    right.current = node;
    setRightNode(node);
  }, []);

  // One measurement serves both panes: they are the same width by construction. It is
  // taken from whichever pane is on screen, so a layout change never leaves it stale.
  const probe = leftNode ?? rightNode;
  useEffect(() => {
    if (!probe) {
      return;
    }
    // The pane label sits above the scroll area, which is what the sheet must fit in.
    const measure = () => setPane({ width: probe.clientWidth, height: probe.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(probe);
    return () => observer.disconnect();
  }, [probe]);

  useEffect(() => {
    setLargeReady(false);
  }, [srcLarge]);

  const ratio = aspect && aspect > 0 ? aspect : (natural ?? 1.4);
  const fit = fitWidth(pane.width, pane.height, ratio, GUTTER);
  const width = Math.round(fit * zoom);
  const height = Math.round(width / ratio);
  const wantLarge = zoom > 1;

  const follow = useCallback(
    (from: HTMLDivElement) => {
      for (const pane of panes()) {
        if (pane === from) {
          continue;
        }
        const to = mirrorScroll(
          {
            left: from.scrollLeft,
            top: from.scrollTop,
            scrollWidth: from.scrollWidth,
            scrollHeight: from.scrollHeight,
            clientWidth: from.clientWidth,
            clientHeight: from.clientHeight,
          },
          pane,
        );
        pane.scrollLeft = to.left;
        pane.scrollTop = to.top;
      }
    },
    [panes],
  );

  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const pane = event.currentTarget;
      if (leader.current && leader.current !== pane) {
        return;
      }
      leader.current = pane;
      follow(pane);
      if (release.current !== undefined) {
        window.clearTimeout(release.current);
      }
      release.current = window.setTimeout(() => {
        leader.current = null;
      }, 80);
    },
    [follow],
  );

  const changeZoom = useCallback(
    (next: Zoom, pane?: HTMLDivElement | null, anchor?: { x: number; y: number }) => {
      setZoom((current) => {
        if (next === current) {
          return current;
        }
        const target = pane ?? panes()[0];
        if (target) {
          pending.current = { ratio: next / current, pane: target, ...(anchor ? { anchor } : {}) };
        }
        return next;
      });
    },
    [panes],
  );

  // After the sheet has been laid out at its new size, put the anchored point back.
  useLayoutEffect(() => {
    const job = pending.current;
    pending.current = null;
    if (!job) {
      return;
    }
    const to = zoomAbout(
      { left: job.pane.scrollLeft, top: job.pane.scrollTop },
      { width: job.pane.clientWidth, height: job.pane.clientHeight },
      job.ratio,
      job.anchor,
    );
    leader.current = job.pane;
    job.pane.scrollLeft = to.left;
    job.pane.scrollTop = to.top;
    follow(job.pane);
    leader.current = null;
  }, [zoom, follow]);

  // Centre both panes on a box when asked to (a panel picked from a list).
  useEffect(() => {
    const pane = panes()[0];
    if (!focusBox || !pane) {
      return;
    }
    const cx = ((focusBox.x0 + focusBox.x1) / 2) * width;
    const cy = ((focusBox.y0 + focusBox.y1) / 2) * height;
    pane.scrollLeft = Math.max(0, cx - pane.clientWidth / 2);
    pane.scrollTop = Math.max(0, cy - pane.clientHeight / 2);
    follow(pane);
    // Only a new box moves the view; a resize must not steal the user's position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusBox]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) {
        return;
      }
      if (event.key === "+" || event.key === "=") {
        changeZoom(stepZoom(zoom, 1));
      } else if (event.key === "-" || event.key === "_") {
        changeZoom(stepZoom(zoom, -1));
      } else if (event.key === "0") {
        changeZoom(1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, changeZoom]);

  useEffect(() => {
    const nodes = [leftNode, rightNode].filter((n): n is HTMLDivElement => n !== null);
    const off = nodes.map((node) => {
      const onWheel = (event: WheelEvent) => {
        if (!(event.ctrlKey || event.metaKey)) {
          return;
        }
        event.preventDefault();
        const rect = node.getBoundingClientRect();
        const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        setZoom((current) => {
          const next = stepZoom(current, event.deltaY < 0 ? 1 : -1);
          if (next !== current) {
            pending.current = { ratio: next / current, pane: node, anchor };
          }
          return next;
        });
      };
      node.addEventListener("wheel", onWheel, { passive: false });
      return () => node.removeEventListener("wheel", onWheel);
    });
    return () => off.forEach((remove) => remove());
  }, [leftNode, rightNode]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    const pane = event.currentTarget;
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      left: pane.scrollLeft,
      top: pane.scrollTop,
      moved: false,
    };
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = drag.current;
    if (!start || event.buttons !== 1) {
      return;
    }
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (!start.moved && Math.hypot(dx, dy) < 4) {
      return;
    }
    start.moved = true;
    const pane = event.currentTarget;
    pane.scrollLeft = start.left - dx;
    pane.scrollTop = start.top - dy;
  };
  const endDrag = () => {
    // Left set for the click that follows a drag, so it does not pick a panel.
    window.setTimeout(() => {
      drag.current = null;
    }, 0);
  };

  const pick = useCallback(
    (instance: Instance) => {
      if (drag.current?.moved) {
        return;
      }
      onPick?.(instance);
    },
    [onPick],
  );

  const hover = useCallback((instance: Instance | null, at: { x: number; y: number } | null) => {
    setTip(instance && at ? { instance, x: at.x, y: at.y } : null);
  }, []);

  const sheet = useMemo(
    () => (
      <>
        <img
          className="absolute inset-0 size-full select-none"
          src={src}
          alt=""
          width={width}
          height={height}
          draggable={false}
          decoding="async"
          onLoad={(event) => {
            const img = event.currentTarget;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              setNatural(img.naturalWidth / img.naturalHeight);
            }
          }}
        />
        {wantLarge ? (
          <img
            className={cn(
              "absolute inset-0 size-full transition-opacity select-none",
              largeReady ? "opacity-100" : "opacity-0",
            )}
            src={srcLarge}
            alt=""
            width={width}
            height={height}
            draggable={false}
            decoding="async"
            onLoad={() => setLargeReady(true)}
          />
        ) : null}
      </>
    ),
    [src, srcLarge, width, height, wantLarge, largeReady],
  );

  const tipPaint = tip ? paint.get(unitOf(tip.instance)) : undefined;
  const showOriginal = layout !== "takeoff";
  const showTakeoff = layout !== "original";

  const scrollProps = {
    onScroll,
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerLeave: endDrag,
    className: "min-h-0 flex-1 cursor-grab overflow-auto bg-stage active:cursor-grabbing",
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 border-b border-line bg-surface px-3 py-1.5">
        <div
          className="flex items-center rounded-lg border border-line bg-sunken p-0.5"
          role="group"
          aria-label="Zoom"
        >
          <button
            type="button"
            aria-label="Zoom out"
            className={ZOOM_BUTTON}
            disabled={zoom === ZOOM_LEVELS[0]}
            onClick={() => changeZoom(stepZoom(zoom, -1))}
          >
            <Minus aria-hidden="true" className="size-3.5" />
          </button>
          {ZOOM_LEVELS.map((level) => (
            <button
              key={level}
              type="button"
              aria-pressed={zoom === level}
              aria-label={level === 1 ? "Zoom to fit" : `Zoom ${level}×`}
              className={cn(
                ZOOM_BUTTON,
                "tabular w-9 text-xs font-medium",
                zoom === level && "bg-surface text-ink shadow-card",
              )}
              onClick={() => changeZoom(level)}
            >
              {level === 1 ? "Fit" : `${level}×`}
            </button>
          ))}
          <button
            type="button"
            aria-label="Zoom in"
            className={ZOOM_BUTTON}
            disabled={zoom === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
            onClick={() => changeZoom(stepZoom(zoom, 1))}
          >
            <Plus aria-hidden="true" className="size-3.5" />
          </button>
        </div>
        <span className="hidden text-xs text-muted xl:inline">
          Drag to move · <Kbd>Ctrl</Kbd> + scroll to zoom · <Kbd>+</Kbd> <Kbd>−</Kbd> <Kbd>0</Kbd>
        </span>
      </div>
      <div className="flex min-h-0 flex-1 divide-x divide-line">
        {showOriginal ? (
          <section className="flex min-w-0 flex-1 flex-col" aria-label={`Original, ${name}`}>
            <h3 className={PANE_LABEL}>Original</h3>
            <div ref={attachLeft} data-testid="pane-original" {...scrollProps}>
              <div className={CENTRE}>
                <div className="relative bg-white shadow-card" style={{ width, height }}>
                  {sheet}
                </div>
              </div>
            </div>
          </section>
        ) : null}
        {showTakeoff ? (
          <section className="flex min-w-0 flex-1 flex-col" aria-label={`Take-off, ${name}`}>
            <h3 className={PANE_LABEL}>
              Take-off
              <span className="tabular font-normal text-muted">
                {instances.length.toLocaleString("en-US")} panels
              </span>
            </h3>
            <div ref={attachRight} data-testid="pane-takeoff" {...scrollProps}>
              <div className={CENTRE}>
                <div className="relative bg-white shadow-card" style={{ width, height }}>
                  {sheet}
                  <PanelOverlay
                    instances={instances}
                    paint={paint}
                    width={width}
                    height={height}
                    highlightUnitId={highlightUnitId}
                    selectedId={selectedId}
                    pickedIds={pickedIds}
                    onPick={pick}
                    onHover={hover}
                  />
                  {onBox ? <BoxSelect onBox={onBox} label={`Box a block on ${name}`} /> : null}
                </div>
              </div>
            </div>
          </section>
        ) : null}
      </div>
      {tip ? (
        <div
          className="pointer-events-none fixed z-40 flex flex-col gap-0.5 rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-pop"
          role="tooltip"
          style={{ left: tip.x + 14, top: tip.y + 14 }}
        >
          <strong className="flex items-center gap-1.5 font-mono text-[13px] text-ink">
            <span
              aria-hidden="true"
              className="size-2.5 rounded-sm"
              style={{ background: tipPaint?.colour ?? "var(--color-muted)" }}
            />
            {tipPaint?.mark ?? "Panel"}
          </strong>
          <span className="tabular text-ink-2">
            {feetInches(tip.instance.width_ft)} × {feetInches(tip.instance.height_ft)}
          </span>
          <span className="text-muted">
            {[
              tip.instance.floor ? `Floor ${tip.instance.floor}` : null,
              tip.instance.bay !== null ? `bay ${tip.instance.bay}` : null,
            ]
              .filter(Boolean)
              .join(", ")}
          </span>
        </div>
      ) : null}
    </div>
  );
}
